'use strict';

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import RecipeIngredients from '@components/Modules/RecipeIngredients.react';
import RecipeInstructions from '@components/Modules/RecipeInstructions.react';
import MealEditControls from '../../actions/MealEditControls.react';
import Popup from '@components/Widgets/Popup.react';
import Merchant from '@components/Recipes/Merchant.react';
import { useAssets } from '../../contexts';

/**
 * Helper function to calculate estimated price per serving for recipes
 */
const getEstimatedPrice = (item) => {
    if (!item) return null;

    // For recipes, only use avg_cost_per_serving
    if (item.avg_cost_per_serving) {
        return item.avg_cost_per_serving;
    }

    return null;
};

/**
 * RecipeContentDisplay - Displays recipe ingredients and instructions sections
 *
 * Extracted from MealDetails to improve component organization and testability
 */
const RecipeContentDisplay = ({
    meal,
    recipe,
    details,
    profile,
    sortedMeals,
    dishIndex
}) => {
    // Get merchants from assets context
    const { merchants } = useAssets();
    
    if (!recipe) return null;

    const recipeDetails = details[recipe.details];
    if (!recipeDetails) return null;

    // Get merchant data
    const merchant = recipe.merchant && merchants && merchants[recipe.merchant.uuid];

    // Define section classes for consistency
    const ingredientsSection = 'first';
    const instructionsSection = 'second';

    return (
        <>
            {/* Recipe Ingredients Section */}
            <div className={classNames('details-section', ingredientsSection)}>
                <header>
                    <div className="section-title">
                        <div className="t2 with-merchant">
                            {merchant ? (
                                <Popup
                                    className="merchant-dropdown"
                                    button={
                                        <>
                                            {sortedMeals.length > 1 ? <span>DISH {dishIndex + 1}:&nbsp;</span> : null}
                                            <span>Recipe by</span> <em>{merchant.name}</em>{' '}
                                            <i className="feather feather-info" />
                                        </>
                                    }
                                >
                                    <Merchant merchant={merchant} />
                                </Popup>
                            ) : (
                                <p className="t2">
                                    {sortedMeals.length > 1 ? `DISH ${dishIndex + 1}: ` : null}
                                    Recipe by <em>Meal Provider</em>
                                </p>
                            )}
                        </div>
                    </div>
                    <MealEditControls
                        meal={meal}
                        content={recipe}
                        sortedMeals={sortedMeals}
                    />
                </header>
                <div className="section-body">
                    <div className="meal-title-container">
                        <div className="meal-title-content">
                            <h3 className="meal-title" data-testid="meal-title">
                                {recipe.title || 'Untitled Recipe'}
                            </h3>
                        </div>

                        {(() => {
                            const estimatedPrice = getEstimatedPrice(recipe);
                            return estimatedPrice ? (
                                <div className="price-per-serving">
                                    <p className="t4">${estimatedPrice.toFixed(2)}/SERVING EST</p>
                                </div>
                            ) : null;
                        })()}
                    </div>

                    <RecipeIngredients
                        recipe={recipe}
                        details={recipeDetails}
                        subdetails={details}
                        scaling={meal.scaling || 1}
                        profile={profile}
                    />
                </div>
            </div>

            {/* Recipe Instructions Section */}
            <div className={classNames('details-section', instructionsSection)}>
                <header>
                    <div className="section-title">
                        <p className="t2">
                            {sortedMeals.length > 1 ? `Dish ${dishIndex + 1} ` : null}Instructions
                        </p>
                    </div>
                </header>
                <div className="section-body">
                    <RecipeInstructions
                        recipe={recipe}
                        details={recipeDetails}
                        subdetails={details}
                        user={profile}
                    />
                </div>
            </div>
        </>
    );
};

RecipeContentDisplay.propTypes = {
    meal: PropTypes.object.isRequired,
    recipe: PropTypes.object.isRequired,
    details: PropTypes.object.isRequired,
    profile: PropTypes.object.isRequired,
    sortedMeals: PropTypes.array.isRequired,
    dishIndex: PropTypes.number.isRequired,
};

export default RecipeContentDisplay;
